import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import reportService from '../../services/reportService';
import { LoadingSpinner } from "../../components";
import PurchaseDialog from './components/PurchaseDialog';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '../../reducers/common.reducer';
import {
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiDownload,
  FiDollarSign,
  FiFileText,
  FiGrid,
  FiSearch,
  FiTarget,
  FiTrendingUp,
  FiShield
} from "react-icons/fi";
import NoData from '../../components/NoData';
import Tooltip from '@mui/material/Tooltip';
import ReportByEventMobile from './mobile/ReportByEventMobile';
import { useWindowWidth } from '@react-hook/window-size';
import CustomCalendarSVG from '../../components/CustomCalendarSVG';
import CustomSearchBtn from '../../components/CustomSearchBtn';
import CustomNoData from '../../components/CustomNoData';

const filterType = [
  { value: "all", label: "All events" },
  { value: "notStarted", label: "Not started" },
  { value: "completed", label: "Completed" },
  { value: "playing", label: "Playing" },
];

const sortingType = [
  { value: "amount-desc", label: "Amount: high to low" },
  { value: "amount-asc", label: "Amount: low to high" },
  { value: "time-asc", label: "Time: earliest first" },
  { value: "time-desc", label: "Time: latest first" },
];

const formatAmount = (value = 0) => Number(value || 0).toLocaleString("en-us");

const getStatusLabel = (status) => {
  switch (status) {
    case 0:
      return "Not started";
    case 1:
      return "Playing";
    case 3:
      return "Completed";
    default:
      return "Event";
  }
};

const getLargestDifferenceSingle = (event) => {
  const bodyAmount = Math.abs(Number(event.bodyDifferenceAmount || 0));
  const netMargin = (event?.homePurchaseAmount || 0 + event?.awayPurchaseAmount || 0) - bodyAmount;
  //const goalAmount = Math.abs(Number(event.goalDifferenceAmount || 0));

   return {
      label: "Single",
      name: event.bodyDifferenceAmount < 0 ? event.awayTeam : event.bodyDifferenceAmount > 0 ? event.homeTeam : "-",
      amount: Math.abs(Number(netMargin || 0)),
    };
};

const getLargestDifferenceTotal = (event) => {
  //const bodyAmount = Math.abs(Number(event.bodyDifferenceAmount || 0));
  const goalAmount = Math.abs(Number(event.goalDifferenceAmount || 0));
  const netMargin = (event?.homePurchaseAmount || 0 + event?.awayPurchaseAmount || 0) - goalAmount;

  return {
    label: "Goal market",
    name: event.goalDifferenceAmount < 0 ? "Goal under" : "Goal over",
    amount: Math.abs(Number(netMargin || 0)),
  };
};

export default function ReportByEvent() {
  const dispatch = useDispatch();
  const onlyWidth = useWindowWidth();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(moment(new Date()).format("YYYY-MM-DD"));
  const [selectedFilter, setSelectedFilter] = useState(filterType[0].value);
  const [selectedSorting, setSelectedSorting] = useState(sortingType[2].value);
  const [searchTerm, setSearchTerm] = useState("");
  const [reportData, setReportData] = useState([]);
  const [selectedId, setSelectedId] = useState(0);
  const [saveLoading, setSaveLoading] = useState(false);
  const [limitAmount, setLimitAmount] = useState(0);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await reportService.reportByEvent(date);

      if (response.status === 200) {
        setReportData(response.data._data || []);
        setLimitAmount(response.data._data[0]?.limitAmount || 0);
        setIsLoading(false);
      }
    }
    catch (error) {
      console.log(error)
    }
    finally {
      setIsLoading(false);
    }
  }

  const handleOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  }

  const handleSave = async (amount, remark, type, func) => {
    try {
      setSaveLoading(true);
      const payload = {
        gamblingTypeId: 1,
        isHome: type === "home",
        isAway: type === "away",
        isOver: type === "over",
        isUnder: type === "under",
        rapidEventId: selectedId,
        selectedDate: reportData[0]?.selectedDate || date,
        remark: remark,
        betAmount: Number(amount)
      }

      const response = await reportService.purchaseVoucher(payload);

      if (response.status === 200) {
        func();
        setOpen(false);
        dispatch(setToastMessage({ type: "success", message: 'Successfully inserted' }));
        await fetchData();
      }
      else {
        dispatch(setToastMessage({ type: "error", message: response.data.message }));
      }
    }
    catch (error) {
      console.log("error>>>", error.message);
    }
    finally {
      setSaveLoading(false);
    }
  }

  const visibleEvents = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = reportData.filter((event) => {
      const matchesStatus =
        selectedFilter === "all" ||
        (selectedFilter === "notStarted" && event.eventStatus === 0) ||
        (selectedFilter === "completed" && event.eventStatus === 3) ||
        (selectedFilter === "playing" && event.eventStatus === 1);

      const matchesSearch =
        !query ||
        [event.leagueName, event.homeTeam, event.awayTeam]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query));

      return matchesStatus && matchesSearch;
    });

    return [...filtered].sort((a, b) => {
      switch (selectedSorting) {
        case "time-asc":
          return moment(a.eventTime).valueOf() - moment(b.eventTime).valueOf();
        case "time-desc":
          return moment(b.eventTime).valueOf() - moment(a.eventTime).valueOf();
        case "amount-asc":
          return getLargestDifferenceSingle(a).amount - getLargestDifferenceSingle(b).amount;
        case "amount-desc":
          return getLargestDifferenceSingle(b).amount - getLargestDifferenceSingle(a).amount;
        default:
          return getLargestDifferenceSingle(b).amount - getLargestDifferenceSingle(a).amount;
      }
    });
  }, [reportData, searchTerm, selectedFilter, selectedSorting]);

  const summary = useMemo(() => {
    const totalSellVolume = reportData.reduce((sum, event) => (
      sum +
      Number(event.homeSaleAmount || 0) +
      Number(event.awaySaleAmount || 0) +
      Number(event.overSaleAmount || 0) +
      Number(event.underSaleAmount || 0)
    ), 0);

    const goalExposure = reportData.reduce((sum, event) => (
      sum +
      Number(event.overSaleAmount || 0) +
      Number(event.underSaleAmount || 0)
    ), 0);

    return {
      eventsFound: reportData.length,
      totalSellVolume,
      goalExposure,
      goalExposurePercent: totalSellVolume ? Math.round((goalExposure / totalSellVolume) * 100) : 0,
    };
  }, [reportData]);

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedFilter(filterType[0].value);
    setSelectedSorting(sortingType[0].value);
  };

  const handleSearch = () => {
    fetchData();
  }

  return (
    <>
      <PurchaseDialog open={open} setOpen={setOpen} handleSave={handleSave} selectedId={selectedId} saveLoading={saveLoading} isMobile={onlyWidth <= 768}/>
      <div className="min-h-screen bg-customLightMilkWhite px-4 py-3 text-customInk dark:bg-custom_dark_background dark:text-customWhite">
        <div className="mb-4 flex items-start justify-between gap-6">
          <div>
            {/* <p className="mb-5 text-[0.7rem] font-bold uppercase tracking-[0.22em] text-customAccentBlue">Trading Intelligence</p> */}
            <h1 className="text-2xl font-semibold tracking-tight text-customInk dark:text-customHeading">Event Report</h1>
            <p className="text-sm text-customSecondaryText dark:text-customMuted">Monitor sell and buy exposure across every scheduled event.</p>
          </div>
          {/* <div className="hidden rounded-2xl border border-customFontLightGreen/20 bg-customFontLightGreen/10 px-4 py-3 text-customFontLightGreen shadow-sm md:flex md:items-center md:gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-customFontLightGreen/15">
              <span className="h-3 w-3 rounded-full bg-customFontLightGreen" />
            </span>
            <div>
              <p className="text-xs font-bold">Data is current</p>
              <p className="mt-1 text-[0.68rem] text-customSecondaryText dark:text-customMuted">Updated just now</p>
            </div>
          </div> */}
          <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="rounded-2xl border border-customTitleColor bg-white px-3 py-2 shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customSky text-customAccentBlue">
                <FiShield className="text-xl" />
              </span>
              <div>
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customMuted">Limit</p>
                <p className="text-2xl font-bold text-customInk dark:text-customHeading">{limitAmount.toLocaleString("us-en") || 0}</p>
                {/* <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">for selected date</p> */}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-customTitleColor bg-white px-3 py-2 shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customSky text-customAccentBlue">
                <FiGrid className="text-xl" />
              </span>
              <div>
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customMuted">Events found</p>
                <p className="text-2xl font-bold text-customInk dark:text-customHeading">{summary.eventsFound}</p>
                {/* <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">for selected date</p> */}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-customTitleColor bg-white px-3 py-2 shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customFontVoilet/10 text-customFontVoilet">
                <FiTrendingUp className="text-xl" />
              </span>
              <div>
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customMuted">Single exposure</p>
                <p className="text-2xl font-bold text-customInk dark:text-customHeading">{formatAmount(summary.totalSellVolume)}</p>
                {/* <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">across all markets</p> */}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-customTitleColor bg-white px-3 py-2 shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customMultiColor/50 text-customFontGreen">
                <FiTarget className="text-xl" />
              </span>
              <div>
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customMuted">Goal exposure</p>
                <p className="text-2xl font-bold text-customInk dark:text-customHeading">{formatAmount(summary.goalExposure)}</p>
                {/* <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">{summary.goalExposurePercent}% of total volume</p> */}
              </div>
            </div>
          </div>
        </div> 
        </div>
    <p className="text-xs font-semibold text-customFontRed">** Alert notifications are sent before each event starts 5 minutes prior. **</p>
         {/* Summary Cards */}
        {/* <div className="mb-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-customTitleColor bg-white px-6 py-3 shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customSky text-customAccentBlue">
                <FiGrid className="text-xl" />
              </span>
              <div>
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customMuted">Events found</p>
                <p className="mt-1 text-2xl font-bold text-customInk dark:text-customHeading">{summary.eventsFound}</p>
                <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">for selected date</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-customTitleColor bg-white px-6 py-3 shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customFontVoilet/10 text-customFontVoilet">
                <FiTrendingUp className="text-xl" />
              </span>
              <div>
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customMuted">Total sell volume</p>
                <p className="mt-1 text-2xl font-bold text-customInk dark:text-customHeading">{formatAmount(summary.totalSellVolume)}</p>
                <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">across all markets</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-customTitleColor bg-white px-6 py-3 shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-customMultiColor/50 text-customFontGreen">
                <FiTarget className="text-xl" />
              </span>
              <div>
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customMuted">Goal exposure</p>
                <p className="mt-1 text-2xl font-bold text-customInk dark:text-customHeading">{formatAmount(summary.goalExposure)}</p>
                <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">{summary.goalExposurePercent}% of total volume</p>
              </div>
            </div>
          </div>
        </div> */}

        <section className="overflow-hidden rounded-2xl border border-customTitleColor bg-white shadow-accountPanel dark:border-customBorder dark:bg-customSurface">
          <div className="grid grid-cols-1 gap-4 border-b border-customTitleColor bg-customGray px-6 py-4 dark:border-customBorder dark:bg-customSurfaceMuted md:grid-cols-[280px_0.8fr_230px_200px_80px]">
            <div className="flex items-center" >
              <label className="block">
                <span className="mb-2 block text-[0.68rem] font-bold text-customSecondaryText dark:text-customMuted">Date</span>
                <div className="relative">
                  <input
                    type="date"
                    value={date}
                    onChange={(event) => setDate(event.target.value)}
                    className="h-10 w-full rounded-xl border border-customTitleColor bg-white pl-3 pr-3 text-xs font-medium text-customInk outline-none transition focus:border-customAccentBlue dark:border-customBorder dark:bg-customSurface dark:text-customWhite"
                  />
                  <CustomCalendarSVG />
                </div>
              </label>
              <div className="mt-[1.58rem]">
              <CustomSearchBtn handleSearch={handleSearch} />
              </div>
            </div>

            <label className="block">
              <span className="mb-2 block text-[0.68rem] font-bold text-customSecondaryText dark:text-customMuted">Search teams or league</span>
              <div className="relative">
                <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-customHeader" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search events..."
                  className="h-10 w-full rounded-xl border border-customTitleColor bg-white pl-11 pr-4 text-xs text-customInk outline-none transition placeholder:text-customHeader focus:border-customAccentBlue dark:border-customBorder dark:bg-customSurface dark:text-customWhite"
                />
              </div>
            </label>

            <label className="block">
              <span className="mb-2 block text-[0.68rem] font-bold text-customSecondaryText dark:text-customMuted">Event type</span>
              <div className="relative">
                <select
                  value={selectedFilter}
                  onChange={(event) => setSelectedFilter(event.target.value)}
                  className="h-10 w-full appearance-none rounded-xl border border-customTitleColor bg-white px-4 pr-10 text-xs font-medium text-customInk outline-none transition focus:border-customAccentBlue dark:border-customBorder dark:bg-customSurface dark:text-customWhite"
                >
                  {filterType.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-customInk dark:text-customWhite" />
              </div>
            </label>

            <label className="block">
              <span className="mb-2 block text-[0.68rem] font-bold text-customSecondaryText dark:text-customMuted">Sort by</span>
              <div className="relative">
                <select
                  value={selectedSorting}
                  onChange={(event) => setSelectedSorting(event.target.value)}
                  className="h-10 w-full appearance-none rounded-xl border border-customTitleColor bg-white px-4 pr-10 text-xs font-medium text-customInk outline-none transition focus:border-customAccentBlue dark:border-customBorder dark:bg-customSurface dark:text-customWhite"
                >
                  {sortingType.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-customInk dark:text-customWhite" />
              </div>
            </label>
             
             <div className="pt-8">
             <button
              type="button"
              onClick={resetFilters}
              className="text-xs font-bold text-customAccentBlue transition hover:text-customFontVoilet"
            >
              Reset filters
            </button>
            </div>
          </div>

          {/* <div className="flex items-center justify-between border-b border-customTitleColor px-6 py-5 dark:border-customBorder">
            <div className="flex items-baseline gap-3">
              <h2 className="text-base font-bold text-customInk dark:text-customHeading">{visibleEvents.length} events</h2>
              <p className="text-xs text-customSecondaryText dark:text-customMuted">Showing report for {moment(date).format("MMM D, YYYY")}</p>
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs font-bold text-customAccentBlue transition hover:text-customFontVoilet"
            >
              Reset filters
            </button>
          </div> */}
        <div className="w-full overflow-x-auto">
        {
          onlyWidth > 768 ?
            <table className="w-full min-w-[1180px] border-collapse">
              <thead>
                <tr className="h-12 border-b border-customTitleColor bg-customGray text-left text-[0.7rem] font-medium uppercase text-customSecondaryText/70 dark:border-customBorder dark:bg-customSurfaceMuted dark:text-customMuted">
                  <th className="w-48 px-6">Event</th>
                  <th className="w-72 px-4">Teams</th>
                  <td className="w-15 px-4"></td>
                  <th className="w-36 px-4">Single<h1>(Sell)</h1></th>
                  <th className="w-36 px-4">T.goal<h1>(Sell)</h1></th>
                  <th className="w-36 px-4">Single<h1>(Buy)</h1></th>
                  <th className="w-36 px-4">T.goal<h1>(Buy)</h1></th>
                  <th className="w-64 px-4">Largest difference</th>
                  <th className="w-44 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
              {
                isLoading ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center">
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) :
                  visibleEvents.length > 0 ?
                    visibleEvents.map((el, i) => {
                      const largestDifference = getLargestDifferenceSingle(el);
                      const largestDifferenceTotal = getLargestDifferenceTotal(el);
                      return (
                        <tr className="h-[86px] border-b border-customTitleColor text-[0.8rem] text-customInk last:border-b-0 dark:border-customBorder dark:text-customWhite" key={el.rapidEventId || i}>
                          <td className="px-3 py-2 align-middle">
                            <p className="max-w-[150px] truncate text-[0.6rem] font-medium uppercase tracking-wide text-customSecondaryText dark:text-customMuted">{el.leagueName}</p>
                            <p className="mt-2 text-sm font-bold text-customInk dark:text-customHeading">{moment(el.eventTime).format("hh:mm A")}</p>
                            <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">{moment(el.eventDate).format("YYYY-MM-DD")}</p>
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <div className="">
                              <p className="flex items-center gap-3 font-bold text-customInk dark:text-customHeading">
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-customFontVoilet/10 text-xs text-customFontVoilet">{el.homeTeam?.charAt(0) || "H"}</span>
                                {el.homeTeam}
                              </p>
                              <p className="flex items-center gap-3 text-customSecondaryText font-semibold dark:text-customMuted mt-2">
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-customMultiColor/60 text-xs text-customFontGreen">{el.awayTeam?.charAt(0) || "A"}</span>
                                {el.awayTeam}
                              </p>
                            </div>
                          </td>
                          <td className="px-3 py-2 align-middle">
                            {/* <div className="flex flex-col items-center gap-2">
                              <span className="rounded-lg bg-customSurfaceMuted/10 px-2 py-1 text-[0.6rem] font-bold text-customSurface">0</span>
                              <span className="rounded-lg bg-customSurfaceMuted/10 px-2 py-1 text-[0.6rem] font-bold text-customSurface">0</span>
                            </div> */}
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <p className="font-bold text-customFontVoilet">{formatAmount(el.homeSaleAmount)}</p>
                            <p className="mt-2 font-bold text-customFontGreen">{formatAmount(el.awaySaleAmount)}</p>
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <p className="font-bold text-customFontVoilet">{formatAmount(el.overSaleAmount)}</p>
                            <p className="mt-2 font-bold text-customFontGreen">{formatAmount(el.underSaleAmount)}</p>
                          </td>
                          <td className="px-3 py-2 align-middle bg-customFontGreen/10">
                            <p className="font-bold text-customInk dark:text-customHeading">{formatAmount(el.homePurchaseAmount)}</p>
                            <p className="mt-2 font-bold text-customInk dark:text-customHeading">{formatAmount(el.awayPurchaseAmount)}</p>
                          </td>
                          <td className="px-3 py-2 align-middle bg-customFontGreen/10">
                            <p className="font-bold text-customInk dark:text-customHeading">{formatAmount(el.overPurchaseAmount)}</p>
                            <p className="mt-2 font-bold text-customInk dark:text-customHeading">{formatAmount(el.underPurchaseAmount)}</p>
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <div className="w-56 rounded-xl border border-customTitleColor bg-customSlate px-4 py-3 dark:border-customBorder dark:bg-customSurfaceMuted">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-[0.64rem] font-bold uppercase tracking-[0.12em] text-customFontVoilet dark:text-customMuted">{largestDifference.name}</p>
                                <p className="font-bold text-customInk dark:text-customHeading">{formatAmount(largestDifference.amount)}</p>
                              </div>
                               <div className="flex items-center justify-between gap-3">
                                <p className="text-[0.64rem] font-bold uppercase tracking-[0.12em] text-customFontGreen dark:text-customMuted">{largestDifferenceTotal.name}</p>
                                <p className="font-bold text-customInk dark:text-customHeading">{formatAmount(largestDifferenceTotal.amount)}</p>
                              </div>
                              {/* <p className="mt-2 truncate text-xs text-customSecondaryText dark:text-customMuted">{largestDifference.name}</p> */}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <div className="flex items-center gap-3">
                              {
                                el.eventStatus === 0 ?
                                  <Tooltip title="Purchase" placement="top" arrow>
                                    <button type="button" onClick={() => handleOpen(el.rapidEventId)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-customTitleColor text-customFontGreen transition hover:border-customFontGreen hover:bg-customFontGreen/10 dark:border-customBorder">
                                      <FiDollarSign className="text-base" />
                                    </button>
                                  </Tooltip> : null
                              }
                              <Tooltip title="View" placement="top" arrow>
                                <button type="button" className="flex h-10 w-10 items-center justify-center rounded-xl border border-customTitleColor text-customInk transition hover:border-customAccentBlue hover:bg-customSky dark:border-customBorder dark:text-customWhite">
                                  <FiFileText className="text-base" />
                                </button>
                              </Tooltip>
                              <Tooltip title={getStatusLabel(el.eventStatus)} placement="top" arrow>
                               <div className="flex flex-col items-center gap-2">
                              {el.eventStatus === 0 && (
                                <span className="rounded-lg bg-customFontVoilet/10 px-2 py-1 text-[0.6rem] font-bold text-customFontVoilet">Not Started</span>
                              )}
                              {el.eventStatus === 1 && (
                                <span className="rounded-lg bg-customFontGreen/10 px-2 py-1 text-[0.6rem] font-bold text-customFontGreen">Playing</span>
                              )}
                              {el.eventStatus === 3 && (
                                <span className="rounded-lg bg-customFontLightGreen/10 px-2 py-1 text-[0.6rem] font-bold text-customFontLightGreen">Finished</span>
                              )}
                            </div>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      )
                    }
                    ) :
                    <tr>
                      <td colSpan={9} className='text-center'>
                         <CustomNoData title="No event report data" description="No betting data found for the selected date range." />
                      </td>
                    </tr>
              }
              </tbody>
            </table>
            :
            <ReportByEventMobile showEvents={visibleEvents} isLoading={isLoading} handleOpen={handleOpen}/>
        }
        </div>

          <div className="flex items-center justify-between border-t border-customTitleColor px-6 py-5 text-xs text-customSecondaryText dark:border-customBorder dark:text-customMuted">
            <p>Showing <span className="font-bold text-customAccentBlue">{visibleEvents.length}</span> of {reportData.length} events</p>
            <p>Amounts shown in MMK</p>
          </div>
        </section>
      </div>
    </>
  )
}


